import { useEffect, useState } from 'react';

import { CMS_API_URL } from '../config/apiBase';

// Process-wide cache so brand settings are only fetched once per session.
const cache = new Map();        // brandSlug -> settings object
const inflight = new Map();     // brandSlug -> Promise

const DEFAULTS = {
  slug: null,
  name: '',
  showSchoolSelector: true,
  openShopEnabled: true,
  deadlineDisplay: 'countdown',
  landingProductsLimit: 8,
  menuItems: [],
  paymentMethods: [],
  seo: { title: '', description: '' },
  showVendorDeliveryMessage: true,
};

function load(brandSlug) {
  if (!brandSlug) return Promise.resolve(DEFAULTS);
  if (cache.has(brandSlug)) return Promise.resolve(cache.get(brandSlug));
  if (inflight.has(brandSlug)) return inflight.get(brandSlug);

  const p = fetch(`${CMS_API_URL}/api/brands/${encodeURIComponent(brandSlug)}`, {
    headers: { Accept: 'application/json' },
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Brand API ${res.status}`);
      const data = await res.json();
      const settings = {
        slug: data.slug ?? brandSlug,
        name: data.name ?? '',
        showSchoolSelector: data.showSchoolSelector !== false,
        // Fail open: an unreachable API or an older BE keeps the open shop
        // available (today's behavior). Only an explicit `false` closes it.
        openShopEnabled: data.openShopEnabled !== false,
        deadlineDisplay: data.deadlineDisplay || 'countdown',
        // The most product cards one front-page block shows (Brands form; default 8).
        landingProductsLimit: Number(data.landingProductsLimit) > 0 ? Number(data.landingProductsLimit) : 8,
        menuItems: Array.isArray(data.menuItems) ? data.menuItems : [],
        paymentMethods: Array.isArray(data.paymentMethods) ? data.paymentMethods : [],
        seo: {
          title: data.seo?.title || '',
          description: data.seo?.description || '',
        },
        // Fail open like the other flags: only an explicit false hides.
        showVendorDeliveryMessage: data.showVendorDeliveryMessage !== false,
      };
      cache.set(brandSlug, settings);
      return settings;
    })
    .catch(() => {
      // Fail open: keep selector visible if the API is unreachable.
      cache.set(brandSlug, { ...DEFAULTS, slug: brandSlug });
      return cache.get(brandSlug);
    })
    .finally(() => inflight.delete(brandSlug));

  inflight.set(brandSlug, p);
  return p;
}

/**
 * Returns brand storefront settings, fetched once per brandSlug.
 *
 *   const { showSchoolSelector, loading } = useBrandSettings('abcse');
 */
export default function useBrandSettings(brandSlug) {
  const [state, setState] = useState(() => ({
    settings: cache.get(brandSlug) ?? { ...DEFAULTS, slug: brandSlug ?? null },
    loading: !cache.has(brandSlug),
  }));

  useEffect(() => {
    let cancelled = false;
    if (!brandSlug) {
      setState({ settings: DEFAULTS, loading: false });
      return () => { cancelled = true; };
    }
    if (cache.has(brandSlug)) {
      setState({ settings: cache.get(brandSlug), loading: false });
      return () => { cancelled = true; };
    }
    setState((s) => ({ ...s, loading: true }));
    load(brandSlug).then((settings) => {
      if (!cancelled) setState({ settings, loading: false });
    });
    return () => { cancelled = true; };
  }, [brandSlug]);

  return { ...state.settings, loading: state.loading };
}
